import {
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  Skeleton,
  Typography,
} from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import { clsx } from 'clsx';
import upperFirst from 'lodash-es/upperFirst';

import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { ShoppingPreferences } from '@jane/bloom/types/schemas/bloom/shoppingPreferences';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';

import styles from './storeSwitcherToggle.module.css';

interface FulfillmentLabelArgs {
  customLanguage: CustomLanguage;
  shoppingPreferences: Nullish<ShoppingPreferences>;
  store: Nullish<BloomMenuStore>;
}

export const fulfillmentLabel = ({
  customLanguage,
  shoppingPreferences,
  store,
}: FulfillmentLabelArgs) => {
  const displayedFulfillmentMode = store?.recreational
    ? customLanguage.storeComplianceLanguageSettings?.['recreational'] || 'Rec'
    : 'Med';
  const displayedReservationMode =
    shoppingPreferences?.reservationMode === 'delivery'
      ? customLanguage.reservationModeLabels.delivery ||
        shoppingPreferences?.reservationMode
      : customLanguage.reservationModeLabels.pickup ||
        shoppingPreferences?.reservationMode;

  return upperFirst(
    `${displayedFulfillmentMode.length > 9 ? `${displayedFulfillmentMode.substring(0, 9)}…` : displayedFulfillmentMode} ${displayedReservationMode}`.toLocaleLowerCase()
  );
};

interface StoreSwitcherToggleProps {
  hidden?: boolean;
  storeNameId?: string;
}

export const StoreSwitcherToggle = ({
  hidden = false,
  storeNameId,
}: StoreSwitcherToggleProps) => {
  const { shouldShowFullSkeletonLoader } = useSkeletonLoader();
  const customLanguage = useCustomLanguage();
  const { isVisible, pinnedStore, toggleVisibility } = useStoreSwitcher();
  const { shoppingPreferences } = useRootLoaderData();

  if (!pinnedStore) {
    return null;
  }

  if (shouldShowFullSkeletonLoader) {
    return (
      <Skeleton animate>
        <Skeleton.Bone className={styles['skeleton']} />
      </Skeleton>
    );
  }

  return (
    <button
      aria-label="toggle store list"
      className={clsx(styles['toggle'], { [styles['toggle--hidden']]: hidden })}
      disabled={hidden}
      onClick={toggleVisibility}
    >
      <Typography
        className={clsx(styles['lineHeight'], styles['grid--title'])}
        color="inherit"
        variant="small-body-regular"
      >
        My store
      </Typography>
      <Typography
        className={clsx(
          styles['lineHeight'],
          styles['storeName'],
          styles['grid--storeName']
        )}
        as="span"
        color="inherit"
        variant="small-body-semibold"
        data-testid="store-switcher-my-store"
        id={storeNameId}
        width="100%"
      >
        {pinnedStore.name}
      </Typography>

      <Flex className={styles['grid--modes']}>
        <Typography
          className={styles['lineHeight']}
          color="inherit"
          variant="small-body-semibold"
          mr={8}
          whiteSpace="nowrap"
          data-testid="store-switcher-reservation-mode"
        >
          {fulfillmentLabel({
            customLanguage,
            shoppingPreferences,
            store: pinnedStore,
          })}
        </Typography>
        {isVisible ? (
          <ChevronUpIcon color="inherit" size="sm" />
        ) : (
          <ChevronDownIcon color="inherit" size="sm" />
        )}
      </Flex>
    </button>
  );
};
